// Почта анкеты — одна нормализация и одна проверка для формы и сервера (16.10.2026).
//
// Sentry 81d4c2f6 «map partner validation: email» (Samsung Browser 30): форма
// проверяла почту маской /.+@.+\..+/, сервер — z.email(). Форма пропускала
// невидимые символы (zero-width из клавиатуры), точку в конце, две точки,
// пробел внутри, кириллицу в домене, а сервер отказывал. Теперь оба зовут
// normalizeEmail и isValidEmail отсюда.
import { z } from "zod";

/**
 * Невидимые и «пробельные» символы, которые клавиатуры и копирование
 * вставляют в почту: NBSP, zero-width (U+200B–U+200D), направления текста
 * (U+200E, U+200F, U+202A–U+202E, U+2066–U+2069), word joiner, BOM,
 * узкие и широкие пробелы.
 */
const INVISIBLE: ReadonlyArray<readonly [number, number]> = [
  [0x00a0, 0x00a0],
  [0x1680, 0x1680],
  [0x180e, 0x180e],
  [0x2000, 0x200f],
  [0x2028, 0x202f],
  [0x205f, 0x2069],
  [0x3000, 0x3000],
  [0xfeff, 0xfeff],
];
// Класс символов собирается из кодов: так в исходнике нет ни одного невидимого символа.
const INVISIBLE_RE = new RegExp(
  "[" + INVISIBLE.map(([a, b]) => String.fromCharCode(a) + (a === b ? "" : "-" + String.fromCharCode(b))).join("") + "]",
  "g",
);

/**
 * Почта как её хранит база: без невидимых символов и без пробелов вообще
 * (в адресе их не бывает — «name @gmail.com» с клавиатуры телефона, Sentry G,
 * 24.10.2026), строчными.
 */
export function normalizeEmail(raw: string): string {
  return raw.replace(INVISIBLE_RE, "").replace(/\s+/g, "").toLowerCase();
}

/** Частые опечатки в домене → правильный домен (подсказка «может быть, …?»). */
const DOMAIN_TYPOS: Record<string, string> = {
  "gmial.com": "gmail.com", "gmai.com": "gmail.com", "gmal.com": "gmail.com", "gamil.com": "gmail.com", "gnail.com": "gmail.com",
  "gmail.co": "gmail.com", "gmail.con": "gmail.com", "gmail.cm": "gmail.com", "gmail.om": "gmail.com", "gmail.ru": "gmail.com", "gmail.az": "gmail.com",
  "mail.r": "mail.ru", "mail.ry": "mail.ru", "yandex.r": "yandex.ru", "yandex.com.ru": "yandex.ru",
  "hotmial.com": "hotmail.com", "hotmail.co": "hotmail.com", "yahoo.co": "yahoo.com", "yaho.com": "yahoo.com",
  "outlook.co": "outlook.com", "icloud.co": "icloud.com", "box.a": "box.az",
};

export type EmailHint = { code: "no_at" | "no_domain" | "cyrillic" | "typo" | "other"; suggest?: string };

/** Что не так с почтой — для понятной подсказки в форме. null — почта в порядке. */
export function emailHint(normalized: string): EmailHint | null {
  if (!normalized) return null;
  // Кириллица в адресе — свойством Unicode, без кириллического литерала (verify:answer-labels).
  if (/\p{Script=Cyrillic}/u.test(normalized)) return { code: "cyrillic" };
  const at = normalized.indexOf("@");
  if (at < 1 || normalized.indexOf("@", at + 1) !== -1) return { code: "no_at" };
  const domain = normalized.slice(at + 1);
  const fix = DOMAIN_TYPOS[domain];
  if (fix) return { code: "typo", suggest: `${normalized.slice(0, at)}@${fix}` };
  if (!/^[^.]+(\.[^.]+)+$/.test(domain)) return { code: "no_domain" };
  return isValidEmail(normalized) ? null : { code: "other" };
}

/** Та же проверка, что на сервере (zod email, не длиннее 200). Принимает уже нормализованную строку. */
const EMAIL = z.string().email().max(200);
export const isValidEmail = (normalized: string): boolean => EMAIL.safeParse(normalized).success;

/** Поле схемы: сначала нормализация, потом проверка — на сервере то же, что в форме. */
export const EmailField = z.preprocess((v) => (typeof v === "string" ? normalizeEmail(v) : v), EMAIL);
